import { Suspense, useEffect, useRef } from 'react';
import { graphql, type PreloadedQuery, usePreloadedQuery, useQueryLoader } from 'react-relay';
import { useModelerContext } from '../context/useModelerContext';
import type { ActiveProcessListsProviderQuery } from './__generated__/ActiveProcessListsProviderQuery.graphql';

export const SpActiveProcessesProvider = () => {
    const eventBus = useModelerContext().get('eventBus');
    const [queryRef, loadQuery] = useQueryLoader<ActiveProcessListsProviderQuery>(query);

    const queryRefRef = useRef(queryRef);
    queryRefRef.current = queryRef;

    useEffect(() => {
        const handler = (_event: any, siteUrl: string, definitionKey: string) => {
            eventBus.fire('activeProcessesFetched', {
                loading: true,
                data: undefined,
                error: undefined,
            });

            loadQuery({
                pageNum: 1,
                pageSize: 100,
                sortBy: 'definitionName',
                sortDir: 'ASC',
                filter: {
                    status: { eq: 'ACTIVE' },
                    siteUrl: { eq: siteUrl },
                    definitionKey: { ne: definitionKey },
                },
            });
        };

        eventBus.on('fetchActiveProcesses', handler);

        return () => {
            eventBus.off('fetchActiveProcesses', handler);
        };
    }, [eventBus, loadQuery]);

    if (!queryRef) return null;

    return (
        <Suspense fallback={null}>
            <ActiveProcessesProvider queryRef={queryRef} />
        </Suspense>
    );
};
const query = graphql`
    query ActiveProcessListsProviderQuery(
        $pageNum: Int
        $pageSize: Int
        $sortBy: String
        $sortDir: SortDirection
        $search: String
        $searchBy: [String]
        $filter: ProcessDefinitionFilterInput
    ) {
        getProcessDefinitions(
            pageNum: $pageNum
            pageSize: $pageSize
            sortBy: $sortBy
            sortDir: $sortDir
            search: $search
            searchBy: $searchBy
            filterInput: $filter
        ) {
            content {
                id
                definitionKey
                definitionName
            }
        }
    }
`;
const ActiveProcessesProvider = ({
    queryRef,
}: {
    queryRef: PreloadedQuery<ActiveProcessListsProviderQuery>;
}) => {
    const queryData = usePreloadedQuery(query, queryRef);
    const eventBus = useModelerContext().get('eventBus');
    const definitions = queryData.getProcessDefinitions?.content ?? [];
    useEffect(() => {
        if (!definitions.length) {
            eventBus.fire('activeProcessesFetched', {
                loading: false,
                data: [],
                error: new Error('Prozesse konnten nicht geladen werden.'),
            });
            return;
        }

        const sorted = [...definitions].sort((a, b) =>
            a.definitionName.localeCompare(b.definitionName)
        );

        eventBus.fire('activeProcessesFetched', {
            data: sorted,
            loading: false,
            error: undefined,
        });
    }, [queryRef]);

    return null;
};
